/**
 * Web Storage API utilities for local and session storage.
 * Provides safe access to browser storage with error handling.
 */

'use strict';

import type { StorageKind } from '../types.js';

/**
 * Get the appropriate storage object based on kind.
 * 
 * @param kind - Storage type ('local' or 'session')
 * @returns Storage object
 */
function getStorage(kind: StorageKind): Storage {
    return kind === 'session' ? window.sessionStorage : window.localStorage;
}

/**
 * Get a value from storage.
 * 
 * @param kind - Storage type ('local' or 'session')
 * @param key - Storage key
 * @returns Stored value or null if not found or error occurred
 */
export function getItem(kind: StorageKind, key: string): string | null {
    try {
        return getStorage(kind)?.getItem(key) ?? null;
    } catch {
        return null;
    }
}

/**
 * Set a value in storage.
 * 
 * @param kind - Storage type ('local' or 'session')
 * @param key - Storage key
 * @param value - Value to store
 * @returns True if successfully stored
 */
export function setItem(kind: StorageKind, key: string, value: string): boolean {
    try {
        getStorage(kind)?.setItem(key, value ?? '');
        return true;
    } catch {
        return false;
    }
}

/**
 * Remove a value from storage.
 * 
 * @param kind - Storage type ('local' or 'session')
 * @param key - Storage key
 * @returns True if successfully removed
 */
export function removeItem(kind: StorageKind, key: string): boolean {
    try {
        getStorage(kind)?.removeItem(key);
        return true;
    } catch {
        return false;
    }
}

/**
 * Clear all values from storage.
 * 
 * @param kind - Storage type ('local' or 'session')
 * @returns True if successfully cleared
 */
export function clear(kind: StorageKind): boolean {
    try {
        getStorage(kind)?.clear();
        return true;
    } catch {
        return false;
    }
}
